/**
 * DCA library templates — deployable strategy / agent config stubs (Advanced+).
 *
 * WORK-PLAN C2/C3: multi-exchange DCA library under the agent model.
 * Templates are strategy configs you deploy as your agents — not signals,
 * not copy-trading feeds, not stranger leaderboards.
 *
 * Honesty rules (PRODUCT / marketing non-goals):
 * - No invented returns, APY, win rates, or backtest P&L.
 * - Exchange lists are placeholders for connectors you will attach.
 * - Defaults are structural config hints only, not performance promises.
 */

export type DcaTemplateId =
  | 'fixed-interval-spot'
  | 'weekly-accumulate'
  | 'limit-ladder-dca'
  | 'volatility-spaced'
  | 'multi-asset-rotate'
  | 'multi-exchange-dca'

/** Structural defaults only — never performance metrics. */
export interface DcaTemplateDefaults {
  /** Cadence label for scheduled buys (config). */
  interval: string
  /** How size is chosen each cycle (config). */
  sizeStyle: string
  /** Order style used to enter (market / limit ladder, etc.). */
  orderStyle: string
}

export interface DcaTemplate {
  id: DcaTemplateId
  name: string
  /** Plain operator description — what the strategy does, not what it returns. */
  description: string
  /**
   * Exchange connector placeholders. Real venue wiring is configured at deploy.
   * Do not treat these as live multi-exchange counts for marketing.
   */
  exchanges: readonly string[]
  kind: 'dca'
  defaults: DcaTemplateDefaults
  /** Optional operator note (risk / use-case), never a performance claim. */
  notes?: string
}

/**
 * Honest template catalog (3–6 stubs). Deploy creates *your* agent/strategy
 * config — not a signal subscription.
 */
export const DCA_TEMPLATES: readonly DcaTemplate[] = [
  {
    id: 'fixed-interval-spot',
    name: 'Fixed-interval spot DCA',
    description:
      'Buys a fixed quote notional on a clock you set (e.g. daily or every N hours). Classic dollar-cost average into a single pair as an agent config.',
    exchanges: ['Exchange A (placeholder)'],
    kind: 'dca',
    defaults: {
      interval: 'Every 24 hours',
      sizeStyle: 'Fixed quote notional per cycle',
      orderStyle: 'Market buy (or best-effort limit)',
    },
    notes: 'Clock-based only — does not skip cycles on drawdowns unless you add risk rules at deploy.',
  },
  {
    id: 'weekly-accumulate',
    name: 'Weekly accumulate DCA',
    description:
      'Lower-frequency schedule for operators who want slower inventory build. Same agent model: your size, your pair, your linked exchange.',
    exchanges: ['Exchange A (placeholder)', 'Exchange B (placeholder)'],
    kind: 'dca',
    defaults: {
      interval: 'Once per week',
      sizeStyle: 'Fixed quote notional per week',
      orderStyle: 'Market buy on schedule',
    },
    notes: 'Fewer fills; still needs funding and exchange connectivity before any live order.',
  },
  {
    id: 'limit-ladder-dca',
    name: 'Limit-ladder DCA',
    description:
      'Places a short ladder of limit buys below last price each cycle instead of a single market slice. Structural entry style — not a promised fill rate.',
    exchanges: ['Exchange A (placeholder)'],
    kind: 'dca',
    defaults: {
      interval: 'Every 12–24 hours',
      sizeStyle: 'Split notional across ladder levels',
      orderStyle: 'Limit ladder below reference',
    },
    notes: 'Unfilled ladder legs stay open until cancel or next cycle rules you configure.',
  },
  {
    id: 'volatility-spaced',
    name: 'Volatility-spaced DCA',
    description:
      'Spaces or sizes cycles using a simple volatility band you define (e.g. wider gaps when ATR is high). Config structure only — not a backtested edge claim.',
    exchanges: ['Exchange A (placeholder)', 'Exchange B (placeholder)'],
    kind: 'dca',
    defaults: {
      interval: 'Adaptive within min/max window',
      sizeStyle: 'Base notional ± band from vol',
      orderStyle: 'Market or limit per policy',
    },
    notes: 'Requires a reference volatility input at deploy; defaults are labels until you set numbers.',
  },
  {
    id: 'multi-asset-rotate',
    name: 'Multi-asset rotate DCA',
    description:
      'Rotates scheduled buys across a short list of pairs you choose, equal or custom weights. Your basket, your agents — not a stranger signal pack.',
    exchanges: ['Exchange A (placeholder)'],
    kind: 'dca',
    defaults: {
      interval: 'Every 24 hours',
      sizeStyle: 'Weight-split quote notional',
      orderStyle: 'Market buy per asset in rotation',
    },
    notes: 'Pair list and weights are operator config; library does not ship performance-ranked assets.',
  },
  {
    id: 'multi-exchange-dca',
    name: 'Multi-exchange DCA',
    description:
      'Same fixed-interval DCA structure with placeholders for more than one connected exchange. Deploy attaches only venues you have linked — not a public multi-venue promise.',
    exchanges: [
      'Exchange A (placeholder)',
      'Exchange B (placeholder)',
      'Exchange C (placeholder)',
    ],
    kind: 'dca',
    defaults: {
      interval: 'Every 24 hours',
      sizeStyle: 'Fixed quote notional per linked venue',
      orderStyle: 'Per-venue market or limit policy',
    },
    notes: 'Advanced plan library surface; live multi-exchange needs connected brokers.',
  },
] as const

export function getDcaTemplate(id: DcaTemplateId): DcaTemplate | undefined {
  return DCA_TEMPLATES.find((t) => t.id === id)
}

export function listDcaTemplates(): readonly DcaTemplate[] {
  return DCA_TEMPLATES
}
